import React, { createContext, useContext, useEffect, useState } from 'react';
import { useApi } from '../api/api';

export interface PanelSection {
  id: number;
  slug: string;
  title: string;
}

export interface PanelComponent {
  id: number;
  slug: string;
  title: string;
}

interface EditModeLookupsContextValue {
  panelSections: PanelSection[];
  panelComponents: PanelComponent[];
  loading: boolean;
}

const EditModeLookupsContext = createContext<EditModeLookupsContextValue | null>(null);

export function EditModeLookupsProvider({ children }: { children: React.ReactNode }) {
  const { getPanelSections, getPageComponents } = useApi();
  const [panelSections, setPanelSections] = useState<PanelSection[]>([]);
  const [panelComponents, setPanelComponents] = useState<PanelComponent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([getPanelSections(), getPageComponents()])
      .then(([sectionsRes, componentsRes]) => {
        if (cancelled) return;
        const sections = sectionsRes?.data ?? [];
        const components = componentsRes?.data ?? [];
        setPanelSections(Array.isArray(sections) ? sections : []);
        setPanelComponents(Array.isArray(components) ? components : []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Error fetching edit mode lookups:', err);
          setPanelSections([]);
          setPanelComponents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getPanelSections, getPageComponents]);

  const value: EditModeLookupsContextValue = {
    panelSections,
    panelComponents,
    loading,
  };

  return (
    <EditModeLookupsContext.Provider value={value}>
      {children}
    </EditModeLookupsContext.Provider>
  );
}

export function useEditModeLookups(): EditModeLookupsContextValue | null {
  return useContext(EditModeLookupsContext);
}
